import React, { useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { toast } from 'react-toastify';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { FailToastContainer } from 'components/Toast/ToastContainer';
import { theme, toRem } from 'design';
import { Align } from 'design/types';
import { useOnClickOutside } from 'hooks';
import { PayPeriodConfirmModal, PayrollTipsExport } from 'modals';
import { IEmployeePayrollTip, IIntegration, IntegrationCategory, IPayPeriod, ISite, ISiteIntegration } from 'types';
import { forceRender, formatIntegrationName, isPayPeriodClosed, sanitizeFileName } from 'utils';
import { CSVFormatter, IPayrollIntegrationExportError, PayrollExportError, PayrollExportValidationType } from 'utils/csvformatter';
import * as API from '../../api';
import { ActionMenu, ActionMenuItem, ActionMenuList } from '../ActionMenu';
import { ModalFactory } from '../Modal';
import Button from './Button';

const BASE_CSV_OPTIONS = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  showTitle: false,
  useTextFile: false,
  useBom: true,
  useKeysAsHeaders: false,
};

interface IExportPayrollTipsCSVButtonProps {
  label?: string;
  disabled?: boolean;
  site?: ISite;
  startDate?: string;
  endDate?: string;
  query?: IApiQueryOptions;
  payPeriod?: IPayPeriod;
}

const Layout = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;

  button.open svg {
    transform: rotate(180deg);
  }
`;

// Below 640px ActionMenu becomes a full-size panel; these keep it the desktop dropdown at every width, aligned to the button's right edge.
const StyledActionMenu = styled(ActionMenu)`
  top: ${toRem(45)};
  right: 0;
  left: auto;
  width: auto;
  min-width: ${toRem(180)};
  height: auto;
  border-radius: ${toRem(8)};
  box-shadow: ${theme.shadow.dialog};
`;

// A file ready to save: its name, the tips and, for a payroll integration's export, the integration.
type PayrollTipsCsv = [fileName: string, payrollTips: IEmployeePayrollTip[], integration?: IIntegration];

const errorToast = () => toast(<FailToastContainer title="Error" message="Unable to download csv, please try again." />);

const ButtonExportPayrollTipsCSVAsync = ({ label = 'Export', disabled, site, startDate, endDate, query, payPeriod }: IExportPayrollTipsCSVButtonProps) => {
  const actionMenuRef = useRef(null);
  const [clicked, setClicked] = useState<boolean>(false);
  const [exportError, setExportError] = useState<IPayrollIntegrationExportError>(null);
  const [exportData, setExportData] = useState<PayrollTipsCsv>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);

  // Filter integrations to get payroll integrations
  const payrollIntegrations = site?.integrations?.filter((integration) => integration.categories?.includes(IntegrationCategory.PAYROLL));

  // Close action menu when click outside
  useOnClickOutside(actionMenuRef, () => setActionMenuOpen(false));

  const [showExportErrorModal, hideExportErrorModal] = useModal(() => {
    if (!exportError) return null;
    const handleModalClose = () => {
      setExportError(null);
      hideExportErrorModal();
    };
    const handleConfirm = async () => {
      setClicked(true);
      // force loading to display since exportCsv is not async
      await forceRender();
      downloadCsv(...exportData);
      setClicked(false);
      handleModalClose();
    };
    const hasMissingIds = exportError.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID;
    return (
      <ModalFactory customModal onClose={handleModalClose} isActive>
        <PayrollTipsExport error={exportError} onClose={handleModalClose} onContinue={hasMissingIds ? handleConfirm : undefined} isProcessing={clicked} />
      </ModalFactory>
    );
  }, [exportError, exportData, clicked]);

  const [showOpenPeriodWarning, hideOpenPeriodWarning] = useModal(
    () => (
      <PayPeriodConfirmModal
        title="This Pay Period Is Still Open"
        message="You're downloading a report for a period that hasn't been closed yet. The data may change if payments are added, edited, or converted before the period is closed."
        confirmLabel="Download Anyway"
        payPeriod={payPeriod}
        onClose={hideOpenPeriodWarning}
        onConfirm={() => {
          hideOpenPeriodWarning();
          exportCsv(...exportData);
        }}
        dataCy="open-pay-period-export-modal"
      />
    ),
    [payPeriod, exportData]
  );

  // Saves the file, toasting if it can't be built.
  const exportCsv = (fileName: string, payrollTips: IEmployeePayrollTip[], integration?: IIntegration) => {
    try {
      // Format the CSV data
      const response = CSVFormatter.employeePayrollTips(site.id, startDate, endDate, payrollTips, integration);
      // Sanitize file name and export CSV
      const sanitizedFileName = sanitizeFileName(fileName);
      const csvExporter = new ExportToCsv({ ...BASE_CSV_OPTIONS, headers: response.headers, filename: sanitizedFileName });
      csvExporter.generateCsv(response?.data?.length > 0 ? response?.data : [[]]);
    } catch {
      errorToast();
    }
  };

  // Saves the file once the export has passed its checks, confirming first for a period that isn't closed.
  const downloadCsv = (...csv: PayrollTipsCsv) => {
    if (!payPeriod || isPayPeriodClosed(payPeriod)) {
      exportCsv(...csv);
    } else {
      setExportData(csv);
      showOpenPeriodWarning();
    }
  };

  const handleExport = async (payrollIntegration?: ISiteIntegration) => {
    const fileName = `${payrollIntegration ? formatIntegrationName(payrollIntegration) : 'PayPeriod'}_${site.name}_${startDate}_${endDate}`;
    try {
      setClicked(true);
      setActionMenuOpen(false);

      // Fetch site integration details and employee payroll tips for date range
      const [integrationDetails, payrollTipsResponse] = await Promise.all([
        payrollIntegration ? API.Integrations.getIntegration(site.id, payrollIntegration.id) : undefined,
        API.Sites.getEmployeePayrollTips(site.id, startDate, endDate, { ...query, q: query.q?.filter((q) => q.key !== 'payment_date'), limit: 0, offset: 0 }),
      ]);

      if (integrationDetails) {
        const validationWarning = CSVFormatter.validateExportPayroll(site.id, payrollTipsResponse.body.data, integrationDetails.body);
        if (validationWarning?.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID) {
          setExportData([fileName, payrollTipsResponse.body.data, integrationDetails.body]);
          throw new PayrollExportError(validationWarning);
        }
      }

      downloadCsv(fileName, payrollTipsResponse.body.data, integrationDetails?.body);
    } catch (err: any) {
      if (err instanceof PayrollExportError) {
        setExportError(err.error);
        showExportErrorModal();
      } else {
        errorToast();
      }
    } finally {
      setClicked(false);
    }
  };

  // Payroll integrations
  if (payrollIntegrations?.length > 0) {
    return (
      <Layout ref={actionMenuRef}>
        <Button
          data-cy="export-integrations-btn"
          appearance="primary"
          size="small"
          label="Export"
          className={actionMenuOpen ? 'open' : ''}
          icon="Dropdown"
          iconPosition={Align.RIGHT}
          isProcessing={clicked}
          disabled={disabled}
          onClick={() => setActionMenuOpen(!actionMenuOpen)}
        />
        <StyledActionMenu data-cy="payroll-export-menu" isVisible={actionMenuOpen} hideMobileHeader>
          <ActionMenuList>
            <ActionMenuItem key="payroll-export-csv" data-cy="payroll-export-menu-action" onClick={() => handleExport()}>
              Export CSV
            </ActionMenuItem>
            {payrollIntegrations?.map((integration) => {
              const integrationName = formatIntegrationName(integration);
              return (
                <ActionMenuItem key={`${integration.type}-${integration.id}`} data-cy="payroll-export-menu-action" onClick={() => handleExport(integration)}>
                  {`Export for ${integrationName}`}
                </ActionMenuItem>
              );
            })}
          </ActionMenuList>
        </StyledActionMenu>
      </Layout>
    );
  }

  return <Button appearance="primary" size="small" label={label} isProcessing={clicked} disabled={disabled} onClick={() => handleExport()} data-cy="export-btn" />;
};

export default ButtonExportPayrollTipsCSVAsync;
